<template>
  <!--
    An original vector head, shaded the way the lessons describe:
    one light source, a core shadow that wraps the form, a halftone,
    a rim of reflected light on the shadow edge, and a cast shadow
    on the neck. Move `lightX` / `lightY` and the whole form re-lights.
  -->
  <svg
    class="headform"
    :viewBox="`0 0 420 560`"
    role="img"
    :aria-label="label"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <radialGradient :id="uid('skin')" :cx="lx" :cy="ly" r="0.86">
        <stop offset="0%" stop-color="#f6f8fa" />
        <stop offset="26%" stop-color="#d3d8de" />
        <stop offset="52%" stop-color="#9aa2ac" />
        <stop offset="76%" stop-color="#4e555f" />
        <stop offset="100%" stop-color="#1a1f26" />
      </radialGradient>

      <!-- reflected light: a bright sliver on the far side of the core shadow -->
      <linearGradient :id="uid('rim')" :x1="1 - lx" :y1="1 - ly" x2="0.5" y2="0.5">
        <stop offset="0%" stop-color="#c6ccd4" stop-opacity="0.85" />
        <stop offset="55%" stop-color="#c6ccd4" stop-opacity="0" />
      </linearGradient>

      <radialGradient :id="uid('cast')" cx="0.5" cy="0.2" r="0.75">
        <stop offset="0%" stop-color="#05070a" stop-opacity="0.92" />
        <stop offset="100%" stop-color="#05070a" stop-opacity="0" />
      </radialGradient>

      <filter :id="uid('soft')" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="3.4" />
      </filter>
      <filter :id="uid('softer')" x="-40%" y="-40%" width="180%" height="180%">
        <feGaussianBlur stdDeviation="8" />
      </filter>

      <clipPath :id="uid('headclip')">
        <ellipse cx="210" cy="250" rx="122" ry="160" />
      </clipPath>
    </defs>

    <!-- neck + shoulders sit behind the head -->
    <g :filter="`url(#${uid('soft')})`" opacity="0.95">
      <path
        d="M168 372 L168 432 Q120 452 96 500 L96 560 L324 560 L324 500 Q300 452 252 432 L252 372 Z"
        fill="#3c434c"
      />
    </g>
    <!-- cast shadow the jaw throws onto the neck -->
    <ellipse cx="210" cy="424" rx="70" ry="34" :fill="`url(#${uid('cast')})`" />

    <!-- the head as a shaded sphere -->
    <ellipse cx="210" cy="250" rx="122" ry="160" :fill="`url(#${uid('skin')})`" />
    <ellipse
      cx="210"
      cy="250"
      rx="122"
      ry="160"
      :fill="`url(#${uid('rim')})`"
      style="mix-blend-mode: screen"
    />

    <g :clip-path="`url(#${uid('headclip')})`">
      <!-- brow ridge shadow -->
      <path
        d="M118 236 Q210 210 302 236 L302 256 Q210 236 118 256 Z"
        fill="#2b323b"
        opacity="0.5"
        :filter="`url(#${uid('softer')})`"
      />

      <!-- eye sockets -->
      <g v-if="features">
        <ellipse cx="163" cy="252" rx="30" ry="16" fill="#20262e" opacity="0.55" :filter="`url(#${uid('soft')})`" />
        <ellipse cx="257" cy="252" rx="30" ry="16" fill="#20262e" opacity="0.55" :filter="`url(#${uid('soft')})`" />

        <!-- iris + pupil + a single catchlight, half in each -->
        <circle cx="163" cy="252" r="10.5" fill="#3a424c" />
        <circle cx="257" cy="252" r="10.5" fill="#3a424c" />
        <circle cx="163" cy="252" r="4.6" fill="#0b0e13" />
        <circle cx="257" cy="252" r="4.6" fill="#0b0e13" />
        <circle :cx="163 + 3.4" :cy="252 - 3.4" r="2.7" fill="#ffffff" />
        <circle :cx="257 + 3.4" :cy="252 - 3.4" r="2.7" fill="#ffffff" />

        <!-- upper lash lines: thick above the iris, tapering out -->
        <path d="M136 246 Q163 236 190 247" stroke="#12161c" stroke-width="3.4" fill="none" stroke-linecap="round" />
        <path d="M230 247 Q257 236 284 246" stroke="#12161c" stroke-width="3.4" fill="none" stroke-linecap="round" />

        <!-- brows -->
        <path d="M132 224 Q163 210 194 220" stroke="#262c34" stroke-width="7" fill="none" stroke-linecap="round" opacity="0.8" :filter="`url(#${uid('soft')})`" />
        <path d="M226 220 Q257 210 288 224" stroke="#262c34" stroke-width="7" fill="none" stroke-linecap="round" opacity="0.8" :filter="`url(#${uid('soft')})`" />

        <!-- nose: three spheres, described only in shadow -->
        <path
          d="M198 262 Q192 300 182 322 Q196 336 210 336 Q224 336 238 322 Q228 300 222 262"
          fill="#2f353d"
          opacity="0.42"
          :filter="`url(#${uid('softer')})`"
        />
        <ellipse cx="189" cy="328" rx="6.5" ry="4.4" fill="#12161c" opacity="0.9" :filter="`url(#${uid('soft')})`" />
        <ellipse cx="231" cy="328" rx="6.5" ry="4.4" fill="#12161c" opacity="0.9" :filter="`url(#${uid('soft')})`" />

        <!-- mouth: dark line between the lips, highlight on the lower lip -->
        <path d="M170 360 Q190 354 210 357 Q230 354 250 360 Q230 366 210 364 Q190 366 170 360" fill="#171c23" opacity="0.85" :filter="`url(#${uid('soft')})`" />
        <path d="M172 360 Q210 350 248 360" stroke="#0a0d11" stroke-width="2.6" fill="none" stroke-linecap="round" />
        <ellipse cx="210" cy="372" rx="17" ry="5" fill="#e9edf1" opacity="0.34" :filter="`url(#${uid('soft')})`" />

        <!-- chin + cheek shadows -->
        <ellipse cx="210" cy="392" rx="30" ry="12" fill="#252b33" opacity="0.5" :filter="`url(#${uid('softer')})`" />
      </g>

      <!-- hair mass, with the band of light across the crown -->
      <g v-if="hair">
        <path
          d="M84 262 Q80 92 210 86 Q340 92 336 262 Q330 172 300 146 Q256 178 210 178 Q164 178 120 146 Q90 172 84 262 Z"
          fill="#141920"
        />
        <path
          d="M112 152 Q210 104 308 154"
          stroke="#9aa2ac"
          stroke-width="16"
          fill="none"
          opacity="0.32"
          :filter="`url(#${uid('softer')})`"
        />
      </g>
    </g>

    <!-- an edge, not an outline: the tone simply stops -->
    <ellipse
      cx="210"
      cy="250"
      rx="122"
      ry="160"
      fill="none"
      stroke="#000"
      stroke-opacity="0.35"
      stroke-width="1"
    />
  </svg>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  lightX: { type: Number, default: 0.68 },
  lightY: { type: Number, default: 0.26 },
  features: { type: Boolean, default: true },
  hair: { type: Boolean, default: true },
  label: { type: String, default: 'A head shaded as a sphere, lit from one side' }
})

const seed = Math.random().toString(36).slice(2, 8)
const uid = (n) => `${n}-${seed}`

const lx = computed(() => props.lightX)
const ly = computed(() => props.lightY)
</script>

<style scoped>
.headform { display: block; width: 100%; height: auto; }
</style>
